// InviteForm — one-question-at-a-time form.
// - Auto-saves on Next via PATCH /api/invitations/:token
// - "Skip this one" posts skipped:true and advances
// - Escape key saves and jumps to /review

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'wouter'
import {
  fetchReactiveFollowups,
  getInvitation,
  getStoredLang,
  saveAnswer,
  type Answer,
  type Pack,
  type ReactiveFollowup,
} from '@/lib/api'
import { findQuestionIndex, flattenQuestions, type FlatQuestion } from '@/lib/pack'
import { QuestionRenderer, type QuestionValue } from '@/components/QuestionRenderer'
import { ProgressBar } from '@/components/ProgressBar'
import { Button } from '@/components/ui/button'
import { VoiceInputButton } from '@/components/VoiceInputButton'
import { FollowupThinking } from '@/components/FollowupThinking'
import { t } from '@/lib/i18n'

export function InviteForm() {
  const params = useParams() as { token: string; sectionKey: string; qId: string }
  const { token, sectionKey, qId } = params
  const [, setLocation] = useLocation()

  const [pack, setPack] = useState<Pack | null>(null)
  const [answers, setAnswers] = useState<Record<string, Answer>>({})
  const [value, setValue] = useState<QuestionValue>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reactive follow-up state — the inline AI-journalist layer.
  const [reactiveThinking, setReactiveThinking] = useState(false)
  const [reactiveQueue, setReactiveQueue] = useState<ReactiveFollowup[]>([])
  const [reactiveIndex, setReactiveIndex] = useState(0)
  const [reactiveSaving, setReactiveSaving] = useState(false)
  // Draft text for the currently-active follow-up. Lives separately from
  // `value` so the base question's state stays intact underneath.
  const [followupText, setFollowupText] = useState('')
  // Timestamp of the most recent local (debounced) save. Drives the visible
  // "Saving… / Saved Xs ago" indicator.
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null)
  const [, setClockTick] = useState(0)

  // Initial load — honour the language chosen on the landing page.
  useEffect(() => {
    let cancelled = false
    getInvitation(token, getStoredLang())
      .then((res) => {
        if (cancelled) return
        if (res.invitation.status === 'completed') {
          setLocation(`/i/${token}/done`)
          return
        }
        setPack(res.pack)
        const map: Record<string, Answer> = {}
        for (const a of res.answers) map[a.question_id] = a
        setAnswers(map)
      })
      .catch(() => {
        if (!cancelled) setError('load_failed')
      })
    return () => {
      cancelled = true
    }
  }, [token, setLocation])

  const flat = useMemo(() => (pack ? flattenQuestions(pack) : []), [pack])
  const currentIdx = useMemo(
    () => (flat.length ? findQuestionIndex(flat, sectionKey, qId) : -1),
    [flat, sectionKey, qId],
  )
  const current: FlatQuestion | undefined = currentIdx >= 0 ? flat[currentIdx] : undefined

  // Hydrate `value` from stored answer when question changes. A local draft
  // (anything typed but not yet sent via Next) wins over the server copy, so
  // a tab close or refresh doesn't lose in-flight prose.
  useEffect(() => {
    if (!current) return
    const draftKey = `bi:draft:${token}:${current.question.id}`
    let draft: QuestionValue | null = null
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem(draftKey) : null
      if (raw) draft = JSON.parse(raw) as QuestionValue
    } catch {
      draft = null
    }
    if (draft) {
      setValue(draft)
      return
    }
    const existing = answers[current.question.id]
    if (existing) {
      setValue({
        answer_text: existing.answer_text,
        answer_choice: existing.answer_choice,
        answer_number: existing.answer_number,
      })
    } else {
      setValue({})
    }
  }, [current, answers, token])

  // Debounced local save for the base question. Persists mid-typing so a
  // tab close or refresh doesn't lose the in-progress answer.
  useEffect(() => {
    if (!current || typeof window === 'undefined') return
    const draftKey = `bi:draft:${token}:${current.question.id}`
    const timer = window.setTimeout(() => {
      const hasContent =
        (value.answer_text ?? '').trim().length > 0 ||
        (value.answer_choice ?? '').length > 0 ||
        value.answer_number != null
      if (hasContent) {
        try {
          window.localStorage.setItem(draftKey, JSON.stringify(value))
          setLastSavedAt(Date.now())
        } catch {
          /* quota, private mode, etc — drop silently */
        }
      } else {
        try { window.localStorage.removeItem(draftKey) } catch { /* noop */ }
      }
    }, 400)
    return () => window.clearTimeout(timer)
  }, [value, current, token])

  // Reset follow-up draft whenever the active follow-up changes, preferring
  // any localStorage draft over the empty default.
  useEffect(() => {
    if (!current || reactiveQueue.length === 0 || typeof window === 'undefined') {
      setFollowupText('')
      return
    }
    const key = `bi:draft:${token}:${current.question.id}-f${reactiveIndex + 1}`
    try {
      const raw = window.localStorage.getItem(key)
      setFollowupText(raw ?? '')
    } catch {
      setFollowupText('')
    }
  }, [reactiveIndex, reactiveQueue.length, current, token])

  // Debounced local save for the active follow-up draft.
  useEffect(() => {
    if (!current || reactiveQueue.length === 0 || typeof window === 'undefined') return
    const key = `bi:draft:${token}:${current.question.id}-f${reactiveIndex + 1}`
    const timer = window.setTimeout(() => {
      if (followupText.trim().length > 0) {
        try {
          window.localStorage.setItem(key, followupText)
          setLastSavedAt(Date.now())
        } catch { /* noop */ }
      } else {
        try { window.localStorage.removeItem(key) } catch { /* noop */ }
      }
    }, 400)
    return () => window.clearTimeout(timer)
  }, [followupText, current, token, reactiveIndex, reactiveQueue.length])

  // Re-render every 10s so the relative "Saved Xs ago" stays fresh.
  useEffect(() => {
    if (lastSavedAt == null) return
    const id = window.setInterval(() => setClockTick((n) => n + 1), 10_000)
    return () => window.clearInterval(id)
  }, [lastSavedAt])

  // Autofocus the input when the current question OR follow-up changes.
  // Removes the extra tap/click on mobile; makes ← → nav land correctly.
  useEffect(() => {
    if (!current) return
    const followupActive = reactiveQueue.length > 0
    const id = followupActive
      ? `q-${current.question.id}-f${reactiveIndex + 1}`
      : `q-${current.question.id}`
    const raf = requestAnimationFrame(() => {
      const el = document.getElementById(id) as
        | (HTMLElement & { focus?: (opts?: { preventScroll?: boolean }) => void })
        | null
      if (!el) return
      try {
        el.focus({ preventScroll: true })
      } catch {
        el.focus?.()
      }
      const maybeInput = el as HTMLInputElement | HTMLTextAreaElement
      if (typeof maybeInput.setSelectionRange === 'function') {
        const n = maybeInput.value?.length ?? 0
        try {
          maybeInput.setSelectionRange(n, n)
        } catch {
          /* non-selectable input types, ignore */
        }
      }
    })
    return () => cancelAnimationFrame(raf)
  }, [current, reactiveThinking, reactiveQueue.length, reactiveIndex])

  const persist = useCallback(
    async (payload: Parameters<typeof saveAnswer>[1]) => {
      setSaving(true)
      try {
        const res = await saveAnswer(token, payload)
        setAnswers((prev) => ({ ...prev, [res.answer.question_id]: res.answer }))
        // Server has the canonical copy now, drop the local draft for this id.
        if (typeof window !== 'undefined') {
          try {
            window.localStorage.removeItem(`bi:draft:${token}:${payload.question_id}`)
          } catch {
            /* noop */
          }
        }
        return true
      } catch {
        setError('save_failed')
        return false
      } finally {
        setSaving(false)
      }
    },
    [token],
  )

  const goTo = useCallback(
    (target: FlatQuestion | 'review') => {
      if (target === 'review') {
        setLocation(`/i/${token}/review`)
        return
      }
      setLocation(
        `/i/${token}/q/${encodeURIComponent(target.sectionKey)}/${encodeURIComponent(target.question.id)}`,
      )
    },
    [setLocation, token],
  )

  const advanceBase = useCallback(() => {
    const nextQ = flat[currentIdx + 1]
    if (nextQ) goTo(nextQ)
    else goTo('review')
  }, [flat, currentIdx, goTo])

  const next = useCallback(async () => {
    if (!current) return
    const ok = await persist({
      question_id: current.question.id,
      answer_text: value.answer_text ?? null,
      answer_choice: value.answer_choice ?? null,
      answer_number: value.answer_number ?? null,
      skipped: false,
    })
    if (!ok) return

    // Reactive layer: fire on any free-text answer (long_text OR short_text)
    // when the answer is substantive enough that a journalist would actually
    // probe it. Every pack is journalistic by default — opt-out only by
    // setting reactive.enabled=false explicitly in the pack JSON.
    // Skipped answers never trigger: if a respondent skipped, they want
    // LESS depth, not more.
    const type = current.question.type
    const answered = (value.answer_text ?? '').trim()
    const isProbeable =
      (type === 'long_text' || type === 'short_text') && answered.length >= 4
    const packReactive = pack?.reactive?.enabled !== false
    if (isProbeable && packReactive) {
      setReactiveThinking(true)
      setReactiveQueue([])
      setReactiveIndex(0)
      try {
        const res = await fetchReactiveFollowups(
          token,
          current.question.id,
          (value.answer_text ?? '').trim(),
        )
        if (res.followups.length > 0) {
          setReactiveQueue(res.followups)
          setReactiveIndex(0)
          setReactiveThinking(false)
          return // stay on current question; render follow-ups
        }
      } catch {
        // Reactive is best-effort; never block the respondent.
      }
      setReactiveThinking(false)
    }
    advanceBase()
  }, [current, value, persist, pack, token, advanceBase])

  // When a follow-up is answered or skipped, advance within the reactive
  // queue; when the queue is exhausted, advance to the next base question.
  const answerFollowup = useCallback(
    async (text: string) => {
      if (reactiveQueue.length === 0 || !current) return
      const fu = reactiveQueue[reactiveIndex]
      const derivedId = `${current.question.id}-f${reactiveIndex + 1}`
      setReactiveSaving(true)
      try {
        await saveAnswer(token, {
          question_id: derivedId,
          answer_text: text,
          answer_choice: null,
          answer_number: null,
          skipped: false,
        })
        if (typeof window !== 'undefined') {
          try {
            window.localStorage.removeItem(`bi:draft:${token}:${derivedId}`)
          } catch {
            /* noop */
          }
        }
      } catch {
        // Persist failure — log locally, still advance so the respondent isn't stuck.
        setError('save_failed')
      } finally {
        setReactiveSaving(false)
      }
      const nextIdx = reactiveIndex + 1
      if (nextIdx < reactiveQueue.length) {
        setReactiveIndex(nextIdx)
      } else {
        setReactiveQueue([])
        setReactiveIndex(0)
        advanceBase()
      }
      // reference fu to keep TS happy even if we don't use it here
      void fu
    },
    [reactiveQueue, reactiveIndex, current, token, advanceBase],
  )

  const skipFollowup = useCallback(() => {
    if (reactiveQueue.length === 0) return
    const nextIdx = reactiveIndex + 1
    if (nextIdx < reactiveQueue.length) {
      setReactiveIndex(nextIdx)
    } else {
      setReactiveQueue([])
      setReactiveIndex(0)
      advanceBase()
    }
  }, [reactiveQueue, reactiveIndex, advanceBase])

  const back = useCallback(() => {
    if (currentIdx <= 0) return
    goTo(flat[currentIdx - 1])
  }, [currentIdx, flat, goTo])

  const skip = useCallback(async () => {
    if (!current) return
    const ok = await persist({
      question_id: current.question.id,
      answer_text: null,
      answer_choice: null,
      answer_number: null,
      skipped: true,
    })
    if (!ok) return
    const nextQ = flat[currentIdx + 1]
    if (nextQ) goTo(nextQ)
    else goTo('review')
  }, [current, persist, flat, currentIdx, goTo])

  // Keyboard nav:
  //   Esc              → save + review
  //   Cmd/Ctrl+Enter   → save + next (works even while typing)
  //   ArrowRight / >   → save + next (only when focus is outside a text field)
  //   ArrowLeft  / <   → back (only when focus is outside a text field)
  useEffect(() => {
    function inTextField(target: EventTarget | null): boolean {
      const el = target as HTMLElement | null
      if (!el) return false
      if (el.isContentEditable) return true
      const tag = el.tagName
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
    }

    function onKey(e: KeyboardEvent) {
      if (!current) return

      // While a follow-up is active, keyboard shortcuts drive the follow-up
      // instead of the base question.
      const followupActive = reactiveQueue.length > 0

      if (e.key === 'Escape') {
        e.preventDefault()
        if (followupActive) return // don't jump to review from mid-follow-up
        void persist({
          question_id: current.question.id,
          answer_text: value.answer_text ?? null,
          answer_choice: value.answer_choice ?? null,
          answer_number: value.answer_number ?? null,
          skipped: false,
        }).then(() => goTo('review'))
        return
      }

      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault()
        if (followupActive) {
          const trimmed = followupText.trim()
          if (trimmed.length > 0) void answerFollowup(trimmed)
          else skipFollowup()
        } else {
          void next()
        }
        return
      }

      // Plain Enter advances — except inside a textarea where Enter = newline.
      if (
        e.key === 'Enter' &&
        !e.shiftKey &&
        !e.altKey &&
        !e.metaKey &&
        !e.ctrlKey
      ) {
        const target = e.target as HTMLElement | null
        const inTextarea = target?.tagName === 'TEXTAREA'
        if (followupActive) {
          // Follow-ups render as long_text — let plain Enter insert newline.
          if (inTextarea) return
          e.preventDefault()
          const trimmed = followupText.trim()
          if (trimmed.length > 0) void answerFollowup(trimmed)
          else skipFollowup()
          return
        }
        const isLongTextFocus =
          current.question.type === 'long_text' && inTextarea
        if (!isLongTextFocus) {
          e.preventDefault()
          void next()
          return
        }
      }

      // Arrow / angle-bracket nav — skip when the user is typing.
      if (inTextField(e.target)) return

      if (e.key === 'ArrowRight' || e.key === '>' || (e.shiftKey && e.key === '.')) {
        e.preventDefault()
        void next()
        return
      }
      if (e.key === 'ArrowLeft' || e.key === '<' || (e.shiftKey && e.key === ',')) {
        e.preventDefault()
        back()
        return
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [
    current,
    value,
    persist,
    goTo,
    next,
    back,
    reactiveQueue.length,
    followupText,
    answerFollowup,
    skipFollowup,
  ])

  const lang = getStoredLang()

  function savedLabel(): string | null {
    if (lastSavedAt == null) return null
    const prefix = t(lang, 'saved_prefix')
    const seconds = Math.max(0, Math.floor((Date.now() - lastSavedAt) / 1000))
    if (seconds < 5) return `${prefix} ${t(lang, 'just_now')}`
    const rtf = new Intl.RelativeTimeFormat(lang, {
      numeric: 'always',
      style: 'short',
    })
    let rel: string
    if (seconds < 60) rel = rtf.format(-seconds, 'second')
    else if (seconds < 3600) rel = rtf.format(-Math.floor(seconds / 60), 'minute')
    else rel = rtf.format(-Math.floor(seconds / 3600), 'hour')
    return `${prefix} ${rel}`
  }

  if (error === 'load_failed') {
    return (
      <main className="min-h-screen bg-cream flex items-center justify-center px-6 py-10">
        <p className="font-serif text-muted-foreground">{t(lang, 'err_load')}</p>
      </main>
    )
  }

  if (!pack) {
    return (
      <main className="min-h-screen bg-cream flex items-center justify-center px-6 py-10">
        <p className="font-serif text-muted-foreground">{t(lang, 'loading')}</p>
      </main>
    )
  }

  if (!current) {
    return (
      <main className="min-h-screen bg-cream flex items-center justify-center px-6 py-10">
        <div className="max-w-md text-center space-y-4">
          <p className="font-serif text-muted-foreground">
            {t(lang, 'wrong_question')}
          </p>
          <Button variant="secondary" onClick={() => goTo('review')}>
            {t(lang, 'go_to_review')}
          </Button>
        </div>
      </main>
    )
  }

  const progressBaseCurrent = currentIdx + 1
  const progressBaseTotal = flat.length
  const progressEffectiveCurrent =
    reactiveQueue.length > 0
      ? progressBaseCurrent + (reactiveIndex + 1) / (reactiveQueue.length + 1)
      : progressBaseCurrent

  const activeFollowup =
    reactiveQueue.length > 0 ? reactiveQueue[reactiveIndex] : null

  // Follow-up is rendered as its own "next question" — same slot as the
  // base question, not as an extra panel below.
  const followupQuestion =
    activeFollowup && current
      ? {
          id: `${current.question.id}-f${reactiveIndex + 1}`,
          type: 'long_text' as const,
          prompt: activeFollowup.prompt,
          required: false,
        }
      : null

  return (
    <main className="min-h-screen bg-cream px-4 sm:px-6 py-6 sm:py-10">
      <div className="mx-auto max-w-xl space-y-6">
        <ProgressBar
          current={Math.round(progressEffectiveCurrent * 10) / 10}
          total={progressBaseTotal}
        />

        {reactiveThinking ? (
          <FollowupThinking />
        ) : followupQuestion ? (
          <>
            <p className="text-[11px] uppercase tracking-[0.22em] text-vermillion/90 font-mono">
              {t(lang, 'ai_notices')}
              {reactiveQueue.length > 1 && (
                <span className="text-muted-foreground normal-case tracking-normal font-sans ml-2">
                  · {t(lang, 'followup_of', {
                    n: reactiveIndex + 1,
                    m: reactiveQueue.length,
                  })}
                </span>
              )}
            </p>

            <QuestionRenderer
              question={followupQuestion}
              value={{ answer_text: followupText }}
              onChange={(next) => setFollowupText(next.answer_text ?? '')}
              voiceInput={
                <VoiceInputButton
                  token={token}
                  disabled={reactiveSaving}
                  onTranscript={(txt) =>
                    setFollowupText((prev) =>
                      prev.trim() ? `${prev.trim()}\n\n${txt}` : txt,
                    )
                  }
                />
              }
            />

            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={skipFollowup}
                className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
                disabled={reactiveSaving}
              >
                {t(lang, 'skip_next_question')}
              </button>
              <p className="text-xs text-muted-foreground">
                {reactiveSaving
                  ? t(lang, 'saving')
                  : savedLabel() ?? t(lang, 'nav_hint')}
              </p>
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-black/5">
              <Button
                type="button"
                onClick={() => {
                  const trimmed = followupText.trim()
                  if (trimmed.length > 0) void answerFollowup(trimmed)
                  else skipFollowup()
                }}
                disabled={reactiveSaving}
                className="bg-vermillion hover:bg-vermillion/90 text-white inline-flex items-center gap-2"
              >
                <span>{t(lang, 'next')}</span>
                <kbd className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded border border-white/40 bg-white/10 px-1 text-[11px] font-mono leading-none text-white/90">
                  ↵
                </kbd>
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              {current.sectionTitle}
            </p>

            <QuestionRenderer
              question={current.question}
              value={value}
              onChange={(next) =>
                setValue((prev) => ({ ...prev, ...next }))
              }
              voiceInput={
                current.question.type === 'long_text' ? (
                  <VoiceInputButton
                    token={token}
                    disabled={saving}
                    onTranscript={(text) =>
                      setValue((prev) => {
                        const existing = (prev.answer_text ?? '').trim()
                        const joined = existing ? `${existing}\n\n${text}` : text
                        return { ...prev, answer_text: joined }
                      })
                    }
                  />
                ) : undefined
              }
            />

            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={skip}
                className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
                disabled={saving}
              >
                {t(lang, 'skip_this_one')}
              </button>
              <p className="text-xs text-muted-foreground">
                {saving
                  ? t(lang, 'saving')
                  : savedLabel() ?? t(lang, 'nav_hint')}
              </p>
            </div>

            {error === 'save_failed' && (
              <p className="text-sm text-destructive">{t(lang, 'err_save')}</p>
            )}

            <div className="flex items-center justify-between gap-3 pt-4 border-t border-black/5">
              <Button
                type="button"
                variant="secondary"
                onClick={back}
                disabled={currentIdx === 0 || saving}
              >
                {t(lang, 'back')}
              </Button>
              <Button
                type="button"
                onClick={next}
                disabled={saving}
                className="bg-vermillion hover:bg-vermillion/90 text-white inline-flex items-center gap-2"
              >
                <span>
                  {currentIdx === flat.length - 1 ? t(lang, 'review') : t(lang, 'next')}
                </span>
                <kbd className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded border border-white/40 bg-white/10 px-1 text-[11px] font-mono leading-none text-white/90">
                  ↵
                </kbd>
              </Button>
            </div>
          </>
        )}
      </div>
    </main>
  )
}

export default InviteForm
